import { ChangeDetectionStrategy, Component, ChangeDetectorRef } from '@angular/core';
import { UrlBuilder } from '../../../core';
import { GridDetails, GridEvent, GridHeader } from '../../tracking-overview/interfaces/grid';
import { RangeKind, DateRangeFilterValue } from '../../../shared/time-frame-filter/timeframe-filter.component';
import { IBreadcrumb, BreadcrumbService } from '../../shared/breadcrumb-service/breadcrumb.service';
import { SampleTrackingStorage } from '../../sample-tracking-storage';
import { NotificationData, NotificationStatus } from '../interface/notification-data';
import { SampleMonitoringStorage } from '../service/sample-monitoring-storage';
import { Observable, forkJoin } from 'rxjs';
import { SampleMonitoringService } from '../service/sample-monitoring-service';
import { TrackingGridService } from '../../../shared/covance-grid/tracking-grid.service';
import * as moment from 'moment';
import { ActivityLogger } from '../../../activities/activity-logger';
import { map, tap } from 'rxjs/operators';

@Component({
    selector: 'sample-monitoring-notifications',
    template: require('./sample-monitoring-notifications.component.html'),
    styles: [require('./sample-monitoring-notifications.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SampleMonitoringNotificationsComponent {

    public gridDetails: GridDetails;
    public pagePerItems = [25, 50, 75, 100, 250, 500];
    public timeFrameRanges: RangeKind[] = [
        { key: 'today', value: 'Today', selected: false },
        { key: 'weekly', value: 'Last 7 days', selected: true },
        { key: 'monthly', value: 'Last 30 days', selected: false },
        { key: 'twoMonths', value: 'Last 60 days', selected: false },
        { key: 'quarterly', value: 'Last 90 days', selected: false },
    ];
    public gridData: NotificationData[] = [];
    public showDataLoading = true;
    public selectedDateRange: DateRangeFilterValue;

    constructor(
        private readonly urlBuilder: UrlBuilder,
        private breadcrumbService: BreadcrumbService,
        private smStorage: SampleMonitoringStorage,
        private sampleTrackingStorage: SampleTrackingStorage,
        private sampleMonitoringService: SampleMonitoringService,
        private changeDetectorRef: ChangeDetectorRef,
        activityLogger: ActivityLogger
    ) {
        this.breadcrumbService.clear();
        let portfolioRoute: IBreadcrumb = {
            label: 'Sample Monitoring Notifications',
            url: this.urlBuilder.esa().sampletracking().sampleMonitoring().notifications().url()
        };
        this.breadcrumbService.AddRouterToBreadCrumb(portfolioRoute);

        this.selectedDateRange = this.sampleMonitoringService.getWeeklyRange();
        this.loadGridData();
        activityLogger.logActivity('View Sample Monitoring Notification - Sample Tracking');
    }

    public timeFrameFilterChange($event: DateRangeFilterValue) {
        if ($event.dateBegin === this.selectedDateRange.dateBegin
            && $event.dateEnd === this.selectedDateRange.dateEnd && $event.rangeType !== 'dateRange') { return; }
        this.selectedDateRange = $event;
        this.loadGridData();
    }

    public onGridItemClick($event: GridEvent) {
        if ($event.header.key === 'accessionContainer') {
            let data = {
                accessionNumber: $event.data.accessionNumber,
                containerNumber: $event.data.containerNumber,
            };
            this.sampleTrackingStorage.sampleSearchData = new Observable(ob => ob.next(data));
            this.urlBuilder.esa().sampletracking().sampleDetails(data.containerNumber).navigate();
            return;
        }
        if (!$event.data.ruleId || $event.data.ruleId === 0) { return; }
        this.urlBuilder.esa().sampletracking().sampleMonitoring().editRuleForm($event.data.ruleId).navigate();
    }

    public loadGridData() {
        this.showDataLoading = false;
        forkJoin([
            this.smStorage.getMonitoringNotificationsGridData(this.selectedDateRange),
            this.smStorage.getMonitoringNotificationsStatus(),
        ])
            .subscribe(([notificationData, notificationStatus]: [NotificationData[], NotificationStatus[]]) => {
                notificationData = notificationData ? notificationData : [];
                const mergedResult = notificationData
                    .map((nd: NotificationData) => (
                        {
                            ...notificationStatus.find((ns: NotificationStatus) => ns.ruleId === nd.ruleId),
                            ...{
                                ...nd,
                                accessionContainer: nd.accessionNumber + '-' + nd.containerNumber,
                                notificationDate: nd.notificationDate ? moment(nd.notificationDate).format('DD-MMM-YYYY hh:mm A') : '',
                            }
                        }));
                this.gridData = mergedResult;
                this.gridDetails = { header: this.getNotificationsColumnDefs(), data: mergedResult };
                this.showDataLoading = true;
                this.changeDetectorRef.markForCheck();
            });
    }

    private getNotificationsColumnDefs(): GridHeader[] {
        return [
            {
                key: 'isNew',
                displayText: 'New',
                sort: null,
                actions: [
                    {
                        cssClass: 'action disabled new',
                        actionType: 0
                    }
                ]
            },
            {
                key: 'accessionContainer',
                displayText: 'Accession-Container Number',
                sort: null,
                isLink: true,
                enableFilter: true,
                enableSearch: true,
            },
            {
                key: 'ruleEvent',
                displayText: 'Event',
                sort: null,
                enableFilter: true,
                enableSearch: true,
            },
            {
                key: 'ruleName',
                displayText: 'View Rule',
                isLink: true,
                sort: null,
                enableFilter: true,
                enableSearch: true,
                dataStyleClass: 'deletedFlag'
            },
            {
                key: 'protocolCode',
                displayText: 'Protocol Number',
                sort: null,
                enableFilter: true,
                enableSearch: true,
            },
            {
                key: 'region',
                displayText: 'Region',
                sort: null,
                enableFilter: true,
                enableSearch: true,
            },
            {
                key: 'projectNumber',
                displayText: 'Project Number',
                sort: null,
                enableFilter: true,
                enableSearch: true,
            },
            {
                key: 'siteNumber',
                displayText: 'Investigator Number',
                sort: null,
                enableFilter: true,
                enableSearch: true,
            },
            {
                key: 'screeningNumber',
                displayText: 'Screening Number',
                sort: null,
                enableFilter: true,
                enableSearch: true,
            },
            {
                key: 'patientNumber',
                displayText: 'Patient Number',
                sort: null,
                enableFilter: true,
                enableSearch: true,
            },
            {
                key: 'currentSampleStatus',
                displayText: 'Current Sample Status',
                sort: null,
                enableFilter: true,
                enableSearch: true,
            },
            {
                key: 'notificationDate',
                displayText: 'Notification Date',
                sort: 'desc',
                enableFilter: true,
                skipConversion: true,
                enableSearch: true,
                isDateTime: true
            },
        ];
    }

}
